import * as Sentry from '@sentry/react';
import { useState } from 'react';
import { ApplicationStatus } from '../api/applicationService';
import { REACT_APP_SENTRY_ENVIRONMENT } from '../config';

/**
 * Utilidades para Sentry - Monitoreo de Errores, Rendimiento y Breadcrumbs
 */

// Constante para habilitar/deshabilitar logs de Sentry
const SENTRY_LOGS_ENABLED = false;

// Variable para rastrear si Sentry ha sido inicializado
let sentryInitialized = false;

/**
 * Función helper para logs condicionales de Sentry
 */
const sentryLog = (level: 'debug' | 'log' | 'info' | 'warn' | 'error', message: string, ...args: any[]) => {
  if (SENTRY_LOGS_ENABLED) {
    console[level](`[Sentry] ${message}`, ...args);
  }
};

/**
 * Verifica si Sentry está disponible y configurado
 */
const isSentryAvailable = (): boolean => {
  // Si ya sabemos que está inicializado, retornar true
  if (sentryInitialized) {
    return true;
  }

  try {
    // Verificar si Sentry está disponible como módulo importado
    if (typeof Sentry !== 'undefined' && typeof Sentry.captureMessage === 'function') {
      // Solo marcar como inicializado, NO enviar mensaje (evita traces innecesarios)
      sentryInitialized = true;
      return true;
    }
    return false;
  } catch (error) {
    sentryLog('debug', 'Sentry no está disponible:', error);
    return false;
  }
};

/**
 * Marcar Sentry como inicializado (llamado desde initSentry)
 */
export const markSentryInitialized = () => {
  sentryInitialized = true;
};

const REDACTED_QUOTE_TOKEN = '[redacted]';

/** quoteID is an access credential. Read it only to strip it back out of Sentry payloads. */
function quoteAccessToken(user?: { id?: string | number; username?: string } | null): string | null {
  try {
    const fromQuery = new URLSearchParams(window.location.search).get('quoteID');
    if (fromQuery) {
      return fromQuery;
    }
  } catch {
    // location.search can throw in non-browser tests
  }

  const id = user?.id == null ? '' : String(user.id);
  const username = user?.username ?? '';
  if (id && username === `quote_${id}`) {
    return id;
  }
  return null;
}

function redactQuoteToken(value: string, token: string | null): string {
  if (!token) {
    return value;
  }
  let redacted = value.split(token).join(REDACTED_QUOTE_TOKEN);
  const encoded = encodeURIComponent(token);
  if (encoded !== token) {
    redacted = redacted.split(encoded).join(REDACTED_QUOTE_TOKEN);
  }
  return redacted;
}

/**
 * Absolute URLs become origin + pathname. Relative URLs stay path-only.
 * Query and hash are dropped. A path that contains the quote token is redacted.
 */
function safeSentryUrl(raw: string, token: string | null, baseOrigin: string): string {
  if (!raw) {
    return raw;
  }
  const absolute = /^[a-z][a-z0-9+.-]*:/i.test(raw) || raw.startsWith('//');
  try {
    const path = redactQuoteToken(new URL(raw, baseOrigin).pathname, token);
    if (!absolute) {
      return path || '/';
    }
    return `${new URL(raw, baseOrigin).origin}${path}`;
  } catch {
    return redactQuoteToken(raw.split(/[?#]/, 1)[0], token);
  }
}

function dropQuoteUser(event: { user?: { id?: string | number; username?: string } }, token: string | null): void {
  const user = event.user;
  if (!user) {
    return;
  }
  const id = user.id == null ? '' : String(user.id);
  const username = user.username ?? '';
  const matchesToken = !!token && (id === token || username === `quote_${token}`);
  const quoteShaped = id !== '' && username === `quote_${id}`;
  if (matchesToken || quoteShaped) {
    delete event.user;
  }
}

const BREADCRUMB_URL_FIELDS = ['url', 'from', 'to', 'referrer'] as const;

function scrubBreadcrumbUrls(
  breadcrumb: { data?: Record<string, unknown> },
  token: string | null,
  baseOrigin: string,
): void {
  const data = breadcrumb.data;
  if (!data) {
    return;
  }
  for (const key of BREADCRUMB_URL_FIELDS) {
    const value = data[key];
    if (typeof value === 'string' && value) {
      data[key] = safeSentryUrl(value, token, baseOrigin);
    }
  }
  if ('http.query' in data) {
    delete data['http.query'];
  }
}

function scrubSentryRequest(
  request: { url?: string; query_string?: unknown; headers?: Record<string, string> } | undefined,
  token: string | null,
  baseOrigin: string,
): void {
  if (!request) {
    return;
  }
  if (request.url) {
    request.url = safeSentryUrl(request.url, token, baseOrigin);
  }
  if (request.query_string !== undefined) {
    delete request.query_string;
  }
  const headers = request.headers;
  if (!headers) {
    return;
  }
  for (const key of Object.keys(headers)) {
    if (key.toLowerCase() === 'referer' && headers[key]) {
      headers[key] = safeSentryUrl(headers[key], token, baseOrigin);
    }
  }
}

const DROPPED_DATA_KEYS = new Set(['href', 'query']);
const CREDENTIAL_ID_KEYS = new Set(['quoteId', 'quote_id', 'quoteID']);

/**
 * Monitoring breadcrumbs must not carry the quote credential, a full href, or a query string.
 * `knownToken` covers an id passed into the helper that is not on the current URL.
 */
function safeMonitorUrl(raw: string): string {
  const urlToken = quoteAccessToken(null);
  let origin = 'http://localhost';
  try {
    origin = window.location.origin;
  } catch {
    // location can throw in non-browser tests
  }
  return safeSentryUrl(raw, urlToken, origin);
}

function scrubMonitorPayload(
  data: Record<string, any>,
  knownToken?: string | null,
): Record<string, any> {
  const urlToken = quoteAccessToken(null);
  let origin = 'http://localhost';
  try {
    origin = window.location.origin;
  } catch {
    // location can throw in non-browser tests
  }
  return scrubMonitorRecord(data, urlToken, knownToken || null, origin);
}

function scrubMonitorRecord(
  data: Record<string, any>,
  urlToken: string | null,
  knownToken: string | null,
  origin: string,
): Record<string, any> {
  const next: Record<string, any> = {};
  for (const [key, value] of Object.entries(data)) {
    if (DROPPED_DATA_KEYS.has(key)) {
      continue;
    }
    if (CREDENTIAL_ID_KEYS.has(key)) {
      next[key] = REDACTED_QUOTE_TOKEN;
      continue;
    }
    next[key] = scrubMonitorValue(key, value, urlToken, knownToken, origin);
  }
  return next;
}

function scrubMonitorValue(
  key: string,
  value: unknown,
  urlToken: string | null,
  knownToken: string | null,
  origin: string,
): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => scrubMonitorValue(key, item, urlToken, knownToken, origin));
  }
  if (value && typeof value === 'object') {
    const proto = Object.getPrototypeOf(value);
    if (proto === Object.prototype || proto === null) {
      return scrubMonitorRecord(value as Record<string, any>, urlToken, knownToken, origin);
    }
    return value;
  }
  if (typeof value !== 'string') {
    return value;
  }
  const urlField = key === 'endpoint' || key === 'url' || key === 'from' || key === 'to' || key === 'referrer';
  let scrubbed = urlField ? safeSentryUrl(value, urlToken, origin) : redactQuoteToken(value, urlToken);
  if (knownToken) {
    scrubbed = redactQuoteToken(scrubbed, knownToken);
  }
  return scrubbed;
}

function redactQuoteTokenTree(value: unknown, token: string | null, seen?: WeakMap<object, unknown>): unknown {
  if (!token || value == null) {
    return value;
  }
  if (typeof value === 'string') {
    return redactQuoteToken(value, token);
  }
  if (typeof value !== 'object') {
    return value;
  }
  const objects = seen ?? new WeakMap<object, unknown>();
  const already = objects.get(value);
  if (already !== undefined) {
    return already;
  }
  if (value instanceof Error) {
    const redacted = {
      name: value.name,
      message: redactQuoteToken(value.message, token),
      stack: typeof value.stack === 'string' ? redactQuoteToken(value.stack, token) : value.stack,
    };
    objects.set(value, redacted);
    return redacted;
  }
  if (Array.isArray(value)) {
    objects.set(value, value);
    for (let index = 0; index < value.length; index += 1) {
      value[index] = redactQuoteTokenTree(value[index], token, objects);
    }
    return value;
  }
  const record = value as Record<string, unknown>;
  objects.set(record, record);
  for (const key of Object.keys(record)) {
    record[key] = redactQuoteTokenTree(record[key], token, objects);
  }
  return record;
}

function redactEventMessageExtraContexts(
  event: { message?: unknown; extra?: unknown; contexts?: unknown },
  token: string | null,
): void {
  if (typeof event.message === 'string') {
    event.message = redactQuoteToken(event.message, token);
  }
  if (event.extra !== undefined) {
    event.extra = redactQuoteTokenTree(event.extra, token);
  }
  if (event.contexts !== undefined) {
    event.contexts = redactQuoteTokenTree(event.contexts, token);
  }
}

/** Removes `?quoteID=` / `&quoteID=` so a logged request URL cannot carry the credential. */
function stripQuoteIdQuery(value: string): string {
  if (!/quoteID=/i.test(value)) {
    return value;
  }
  return value
    .replace(/([?&])quoteID=[^&#\s]*/gi, (_match, separator: string) => (separator === '?' ? '?' : ''))
    .replace(/\?&/g, '?')
    .replace(/[?&](?=$|[\s#])/g, '');
}

function scrubConsoleText(value: string, token: string | null): string {
  return redactQuoteToken(stripQuoteIdQuery(value), token);
}

/**
 * Quote tokens also show up in Sentry Logs, often without `?quoteID=` on the page.
 * `token: <value>` is the API error text. A long token-like segment after
 * quotation/application is a path id such as `application/<quoteID>/status`.
 */
function redactEmbeddedQuoteSecrets(value: string): string {
  const withoutQuery = stripQuoteIdQuery(value);
  const withoutLabeledToken = withoutQuery.replace(/\btoken:\s*[^\s,;]+/gi, `token: ${REDACTED_QUOTE_TOKEN}`);
  return withoutLabeledToken.replace(
    /\b((?:quotation|application)s?)([/\s]+)(?=[A-Za-z0-9._~+%-]*\d)([A-Za-z0-9._~+%-]{16,})/gi,
    (_match, word: string, separator: string) => `${word}${separator}${REDACTED_QUOTE_TOKEN}`,
  );
}

function redactLogText(value: string, token: string | null): string {
  return redactQuoteToken(redactEmbeddedQuoteSecrets(value), token);
}

function redactLogAttributes(value: unknown, token: string | null, seen?: WeakSet<object>): unknown {
  if (typeof value === 'string') {
    return redactLogText(value, token);
  }
  if (!value || typeof value !== 'object') {
    return value;
  }
  const objects = seen ?? new WeakSet<object>();
  if (objects.has(value)) {
    return value;
  }
  objects.add(value);
  if (Array.isArray(value)) {
    return value.map((item) => redactLogAttributes(item, token, objects));
  }
  const record = value as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    record[key] = CREDENTIAL_ID_KEYS.has(key)
      ? REDACTED_QUOTE_TOKEN
      : redactLogAttributes(record[key], token, objects);
  }
  return record;
}

function redactSentryLog<T extends { message?: unknown; attributes?: Record<string, unknown> }>(
  log: T,
  token: string | null,
): T {
  if (typeof log.message === 'string') {
    log.message = redactLogText(log.message, token);
  }
  if (log.attributes) {
    redactLogAttributes(log.attributes, token);
  }
  return log;
}

function redactCredentialKeys(value: unknown, seen?: WeakSet<object>): void {
  if (!value || typeof value !== 'object') {
    return;
  }
  const objects = seen ?? new WeakSet<object>();
  if (objects.has(value)) {
    return;
  }
  objects.add(value);
  if (Array.isArray(value)) {
    value.forEach((item) => redactCredentialKeys(item, objects));
    return;
  }
  const record = value as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    if (CREDENTIAL_ID_KEYS.has(key)) {
      record[key] = REDACTED_QUOTE_TOKEN;
      continue;
    }
    redactCredentialKeys(record[key], objects);
  }
}

function scrubQuotedPayload(value: unknown, token: string | null, seen?: WeakSet<object>): unknown {
  if (typeof value === 'string') {
    return scrubConsoleText(value, token);
  }
  if (!value || typeof value !== 'object') {
    return value;
  }
  const objects = seen ?? new WeakSet<object>();
  if (objects.has(value)) {
    return value;
  }
  objects.add(value);
  if (value instanceof Error) {
    return {
      name: value.name,
      message: redactQuoteToken(value.message, token),
      stack: typeof value.stack === 'string' ? redactQuoteToken(value.stack, token) : value.stack,
    };
  }
  if (Array.isArray(value)) {
    return value.map((item) => scrubQuotedPayload(item, token, objects));
  }
  const record = value as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    record[key] = CREDENTIAL_ID_KEYS.has(key)
      ? REDACTED_QUOTE_TOKEN
      : scrubQuotedPayload(record[key], token, objects);
  }
  return record;
}

function scrubConsoleBreadcrumb(
  breadcrumb: { message?: unknown; data?: unknown },
  token: string | null,
): void {
  if (typeof breadcrumb.message === 'string') {
    breadcrumb.message = scrubConsoleText(breadcrumb.message, token);
  }
  if (breadcrumb.data !== undefined) {
    breadcrumb.data = scrubQuotedPayload(breadcrumb.data, token);
  }
}

function redactBreadcrumbText(
  breadcrumb: { category?: string; message?: unknown; data?: unknown },
  token: string | null,
): void {
  if (breadcrumb.category === 'console') {
    scrubConsoleBreadcrumb(breadcrumb, token);
    return;
  }
  if (typeof breadcrumb.message === 'string') {
    breadcrumb.message = redactQuoteToken(breadcrumb.message, token);
  }
  redactCredentialKeys(breadcrumb.data);
}

function redactExceptionValues(
  event: { exception?: { values?: Array<{ value?: unknown }> } },
  token: string | null,
): void {
  const values = event.exception?.values;
  if (!Array.isArray(values)) {
    return;
  }
  for (const entry of values) {
    if (entry && typeof entry.value === 'string') {
      entry.value = redactQuoteToken(entry.value, token);
    }
  }
}

function scrubApiContexts(contexts: unknown, token: string | null): void {
  if (!contexts || typeof contexts !== 'object') {
    return;
  }
  const bag = contexts as Record<string, unknown>;
  for (const name of ['api_request', 'api_response']) {
    const context = bag[name];
    if (!context || typeof context !== 'object') {
      continue;
    }
    const record = context as Record<string, unknown>;
    for (const field of ['body', 'data']) {
      if (field in record) {
        record[field] = scrubQuotedPayload(record[field], token);
      }
    }
  }
}

/**
 * Inicializa Sentry con configuración completa para OLE Web Application
 */
export const initSentry = (dsn: string) => {
  if (!REACT_APP_SENTRY_ENVIRONMENT) {
    return;
  }

  Sentry.init({
    dsn,
    // Configuraciones principales
    sendDefaultPii: false,
    environment: REACT_APP_SENTRY_ENVIRONMENT,
    // Habilitar sistema de Logs de Sentry (para ver console.log/warn/etc en dashboard)
    enableLogs: true,
    // Deshabilitar traces automáticos (solo errores generarán traces)
    tracesSampleRate: 0,
    // Configurar integraciones disponibles en Sentry v10.10.0
    integrations: [
      // Captura automática de console.log/warn/error/info/debug como LOGS (NO como eventos)
      Sentry.consoleLoggingIntegration({ levels: ['log', 'warn', 'error', 'info', 'debug'] }),
      // NOTA: browserTracingIntegration removido para evitar traces automáticos de navegación
      // Captura automática de errores globales
      Sentry.globalHandlersIntegration({
        onerror: true,
        onunhandledrejection: true,
      }),
      // Captura de breadcrumbs de consola
      Sentry.breadcrumbsIntegration({
        console: true,
        dom: true,
        fetch: true,
        history: true,
        sentry: true,
        xhr: true,
      }),
    ],
    // Configurar releases para mejor tracking
    release: process.env.REACT_APP_VERSION || '1.0.0',
    beforeSendLog(log) {
      return redactSentryLog(log, quoteAccessToken(null));
    },
    // Configurar breadcrumbs para mejor debugging
    beforeSend(event, hint) {
      // Marcar Sentry como inicializado cuando se envía el primer evento
      if (!sentryInitialized) {
        markSentryInitialized();
      }
      // Agregar contexto adicional a los errores
      sentryLog('log', 'Error capturado por Sentry:', event.exception?.values?.[0]?.value);

      const quoteToken = quoteAccessToken(event.user);
      const pageOrigin = window.location.origin;
      dropQuoteUser(event, quoteToken);
      const pageUrl = safeSentryUrl(window.location.href, quoteToken, pageOrigin);

      // Agregar contexto de la aplicación
      event.tags = {
        ...event.tags,
        app_version: process.env.REACT_APP_VERSION || '1.0.0',
        user_agent: navigator.userAgent,
        url: pageUrl,
        timestamp: new Date().toISOString(),
      };

      scrubSentryRequest(event.request, quoteToken, pageOrigin);
      event.breadcrumbs?.forEach((breadcrumb) => {
        scrubBreadcrumbUrls(breadcrumb, quoteToken, pageOrigin);
        redactBreadcrumbText(breadcrumb, quoteToken);
      });

      // Agregar contexto del navegador
      event.contexts = {
        ...event.contexts,
        browser: {
          name: navigator.userAgent,
          language: navigator.language,
          platform: navigator.platform,
          cookieEnabled: navigator.cookieEnabled,
          online: navigator.onLine,
        },
        page: {
          url: pageUrl,
          referrer: document.referrer ? safeSentryUrl(document.referrer, quoteToken, pageOrigin) : document.referrer,
          title: document.title,
        },
        device: {
          screen_resolution: `${window.screen.width}x${window.screen.height}`,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          pixel_ratio: window.devicePixelRatio,
        },
      };

      // Agregar información de localStorage relevante (sin datos sensibles)
      try {
        const localStorageKeys = Object.keys(localStorage);
        event.contexts.storage = {
          keys_count: localStorageKeys.length,
          has_quote_data: localStorageKeys.some(key => key.includes('quote')),
          has_form_data: localStorageKeys.some(key => key.includes('form')),
        };
      } catch (error) {
        console.warn('Error accessing localStorage for Sentry context:', error);
      }

      // Agregar contexto de la sesión de navegación
      event.contexts.session = {
        session_start: new Date().toISOString(),
        page_load_time: performance.now(),
        navigation_type: performance.navigation.type,
      };

      redactEventMessageExtraContexts(event, quoteToken);
      redactExceptionValues(event, quoteToken);
      scrubApiContexts(event.contexts, quoteToken);

      return event;
    },
    // Capturar errores no manejados con mejor contexto
    beforeBreadcrumb(breadcrumb, hint) {
      const quoteToken = quoteAccessToken(null);
      const pageOrigin = window.location.origin;

      // Personalizar breadcrumbs
      if (breadcrumb.category === 'console') {
        breadcrumb.message = `Console: ${breadcrumb.message ?? ''}`;
        scrubConsoleBreadcrumb(breadcrumb, quoteToken);
      }

      // Agregar timestamp si no existe
      if (!breadcrumb.timestamp) {
        breadcrumb.timestamp = Date.now() / 1000;
      }

      // Agregar contexto adicional a breadcrumbs de navegación
      if (breadcrumb.category === 'navigation') {
        breadcrumb.data = {
          ...breadcrumb.data,
          user_agent: navigator.userAgent,
          referrer: document.referrer,
        };
      }

      // Agregar contexto a breadcrumbs de UI
      if (breadcrumb.category === 'ui.click' || breadcrumb.category === 'ui.input') {
        breadcrumb.data = {
          ...breadcrumb.data,
          url: window.location.href,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
        };
      }

      scrubBreadcrumbUrls(breadcrumb, quoteToken, pageOrigin);
      if (breadcrumb.category !== 'console') {
        redactBreadcrumbText(breadcrumb, quoteToken);
      }

      return breadcrumb;
    },
    // Configurar muestreo para diferentes tipos de errores
    sampleRate: 1.0, // Capturar todos los errores
    tracesSampler: (samplingContext) => {
      // Muestrear más transacciones de error
      if (samplingContext.request?.url?.includes('/api/')) {
        return 0.5; // 50% de las llamadas API
      }
      return 0.1; // 10% del resto
    },
  });
};

// ==================== 1. MONITOREO DE ERRORES ====================

/**
 * Captura un error personalizado con contexto adicional
 */
export const captureError = (
  error: Error | string,
  context?: Record<string, any>,
  tags?: Record<string, string>
) => {
  if (!isSentryAvailable()) {
    sentryLog('debug', 'Sentry no está disponible, omitiendo captureError');
    return;
  }

  Sentry.withScope((scope) => {
    // Agregar contexto adicional
    if (context) {
      scope.setContext('additional_info', context);
    }

    // Agregar tags para filtrado
    if (tags) {
      Object.entries(tags).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
    }

    // Agregar información del quote si existe
    const quoteInfo = getQuoteInfo();
    if (quoteInfo) {
      scope.setUser(quoteInfo);
    }

    Sentry.captureException(error instanceof Error ? error : new Error(error));
  });
};

/**
 * Captura errores de API con información detallada
 */
export const captureApiError = (
  error: any,
  endpoint: string,
  method: string = 'GET',
  requestData?: any
) => {
  if (!isSentryAvailable()) {
    sentryLog('debug', 'Sentry no está disponible, omitiendo captureApiError');
    return;
  }

  const safeEndpoint = safeMonitorUrl(endpoint);
  Sentry.withScope((scope) => {
    scope.setTag('error_type', 'api_error');
    scope.setTag('endpoint', safeEndpoint);
    scope.setTag('method', method);

    scope.setContext('api_request', {
      endpoint: safeEndpoint,
      method,
      data: requestData && typeof requestData === 'object' ? scrubMonitorPayload(requestData) : requestData,
      timestamp: new Date().toISOString()
    });

    if (error.response) {
      scope.setContext('api_response', {
        status: error.response.status,
        statusText: error.response.statusText,
        data: scrubQuotedPayload(error.response.data, quoteAccessToken(null)),
      });
    }

    Sentry.captureException(error);
  });
};

/**
 * Captura errores de React con información del componente
 */
export const captureReactError = (
  error: Error,
  errorInfo: any,
  componentName?: string
) => {
  if (!isSentryAvailable()) {
    sentryLog('debug', 'Sentry no está disponible, omitiendo captureReactError');
    return;
  }

  Sentry.withScope((scope) => {
    scope.setTag('error_type', 'react_error');
    if (componentName) {
      scope.setTag('component', componentName);
    }

    scope.setContext('react_error', {
      componentStack: errorInfo.componentStack,
      errorBoundary: true
    });

    Sentry.captureException(error);
  });
};

// ==================== 2. MONITOREO DE RENDIMIENTO ====================


/**
 * Monitorea el tiempo de ejecución de una función
 * NOTA: Solo usa breadcrumbs, NO genera traces
 */
export const measureFunctionPerformance = async <T>(
  functionName: string,
  fn: () => Promise<T> | T
): Promise<T> => {
  const startTime = performance.now();

  try {
    const result = await fn();
    const duration = performance.now() - startTime;

    if (isSentryAvailable()) {
      addBreadcrumb(
        `Function executed: ${functionName}`,
        'function_performance',
        'info',
        { functionName, duration, success: true, timestamp: new Date().toISOString() }
      );
    }

    return result;
  } catch (error) {
    const duration = performance.now() - startTime;

    if (isSentryAvailable()) {
      addBreadcrumb(
        `Function failed: ${functionName}`,
        'function_performance',
        'error',
        { functionName, duration, success: false, error: error instanceof Error ? error.message : String(error), timestamp: new Date().toISOString() }
      );
    }

    throw error;
  }
};

/**
 * Monitorea llamadas a API
 * NOTA: Solo usa breadcrumbs, NO genera traces
 */
export const measureApiCall = async <T>(
  endpoint: string,
  method: string,
  apiCall: () => Promise<T>
): Promise<T> => {
  const startTime = performance.now();
  const safeEndpoint = safeMonitorUrl(endpoint);

  try {
    const result = await apiCall();
    const duration = performance.now() - startTime;

    if (isSentryAvailable()) {
      addBreadcrumb(
        `API call: ${method} ${safeEndpoint}`,
        'api_call',
        'info',
        scrubMonitorPayload({ endpoint: safeEndpoint, method, duration, success: true, timestamp: new Date().toISOString() })
      );
    }

    return result;
  } catch (error) {
    const duration = performance.now() - startTime;

    if (isSentryAvailable()) {
      addBreadcrumb(
        `API call failed: ${method} ${safeEndpoint}`,
        'api_call',
        'error',
        scrubMonitorPayload({
          endpoint: safeEndpoint,
          method,
          duration,
          success: false,
          error: error instanceof Error ? error.message : String(error),
          timestamp: new Date().toISOString(),
        })
      );
    }

    throw error;
  }
};

// ==================== 3. BREADCRUMBS Y RASTREO DE SESIONES ====================

/**
 * Agrega un breadcrumb personalizado
 */
export const addBreadcrumb = (
  message: string,
  category: string = 'custom',
  level: 'info' | 'warning' | 'error' = 'info',
  data?: Record<string, any>
) => {
  if (!isSentryAvailable()) {
    sentryLog('debug', 'Sentry no está disponible, omitiendo addBreadcrumb');
    return;
  }

  Sentry.addBreadcrumb({
    message,
    category,
    level,
    data,
    timestamp: Date.now() / 1000
  });
};

/**
 * Breadcrumb para acciones del usuario
 */
export const trackUserAction = (
  action: string,
  element?: string,
  additionalData?: Record<string, any>
) => {
  if (!isSentryAvailable()) {
    sentryLog('debug', 'Sentry no está disponible, omitiendo trackUserAction');
    return;
  }

  addBreadcrumb(
    `User action: ${action}`,
    'user_action',
    'info',
    {
      action,
      element,
      ...additionalData,
      timestamp: new Date().toISOString()
    }
  );
};

/**
 * Breadcrumb para navegación
 */
export const trackNavigation = (
  from: string,
  to: string,
  method: 'push' | 'replace' | 'back' | 'forward' = 'push'
) => {
  if (!isSentryAvailable()) {
    sentryLog('debug', 'Sentry no está disponible, omitiendo trackNavigation');
    return;
  }

  addBreadcrumb(
    `Navigation: ${from} → ${to}`,
    'navigation',
    'info',
    {
      from,
      to,
      method,
      timestamp: new Date().toISOString()
    }
  );
};

/**
 * Breadcrumb para cambios de estado
 */
export const trackStateChange = (
  component: string,
  previousState: any,
  newState: any,
  action?: string
) => {
  if (!isSentryAvailable()) {
    sentryLog('debug', 'Sentry no está disponible, omitiendo trackStateChange');
    return;
  }

  addBreadcrumb(
    `State change in ${component}`,
    'state_change',
    'info',
    {
      component,
      action,
      previousState: typeof previousState === 'object' ?
        JSON.stringify(previousState) : previousState,
      newState: typeof newState === 'object' ?
        JSON.stringify(newState) : newState,
      timestamp: new Date().toISOString()
    }
  );
};

/**
 * Breadcrumb para eventos de formulario
 */
export const trackFormEvent = (
  formName: string,
  event: 'submit' | 'reset' | 'change' | 'focus' | 'blur',
  field?: string,
  value?: any
) => {
  if (!isSentryAvailable()) {
    sentryLog('debug', 'Sentry no está disponible, omitiendo trackFormEvent');
    return;
  }

  addBreadcrumb(
    `Form ${event}: ${formName}`,
    'form_event',
    'info',
    {
      formName,
      event,
      field,
      value: typeof value === 'object' ? JSON.stringify(value) : value,
      timestamp: new Date().toISOString()
    }
  );
};

// ==================== UTILIDADES AUXILIARES ====================

/**
 * Obtiene información del quoteID desde query params
 */
const getQuoteInfo = () => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const quoteId = urlParams.get('quoteID');

    if (quoteId) {
      return {
        id: quoteId,
        username: `quote_${quoteId}`,
        type: 'quote_session'
      };
    }
  } catch (error) {
    console.warn('Error getting quote ID for Sentry:', error);
  }
  return null;
};

/**
 * Captures a message without the quoteID. The id is an access credential and
 * is never prefixed onto the message.
 */
export const captureMessageWithQuoteId = (message: string, options?: any) => {
  return Sentry.captureMessage(redactQuoteToken(message, quoteAccessToken(null)), options);
};

/**
 * Configura información del quote en Sentry
 */
export const setQuoteInfo = (quote: {
  id: string;
  type?: string;
  [key: string]: any;
}) => {
  const { id, ...otherProps } = quote;
  Sentry.setUser({
    id: id,
    username: `quote_${id}`,
    ...otherProps,
  });
};

/**
 * Configura información del usuario en Sentry (para casos especiales)
 */
export const setUserInfo = (user: {
  id?: string;
  email?: string;
  username?: string;
  [key: string]: any;
}) => {
  Sentry.setUser(user);
};

/**
 * Limpia información del usuario
 */
export const clearUserInfo = () => {
  Sentry.setUser(null);
};

/**
 * Agrega contexto global a Sentry
 */
export const setGlobalContext = (key: string, context: Record<string, any>) => {
  Sentry.setContext(key, context);
};

/**
 * Configura tags globales
 */
export const setGlobalTag = (key: string, value: string) => {
  Sentry.setTag(key, value);
};

/**
 * Captura errores con contexto completo de la aplicación OLE
 */
export const captureOLEError = (
  error: Error | string,
  context?: {
    component?: string;
    action?: string;
    page?: string;
    /** Ignored. A quote access token must not be attached to the Sentry event. */
    userId?: string;
    formData?: any;
    apiEndpoint?: string;
    additionalData?: Record<string, any>;
  }
) => {
  if (!isSentryAvailable()) {
    sentryLog('debug', 'Sentry no está disponible, omitiendo captureOLEError');
    return;
  }

  Sentry.withScope((scope) => {
    const quoteToken = quoteAccessToken(null);
    const pageOrigin = window.location.origin;

    // Tags específicos de OLE
    scope.setTag('app', 'ole-web-application');
    scope.setTag('component', context?.component || 'unknown');
    const rawPage = context?.page || window.location.pathname;
    const safePage = rawPage.includes('?') || rawPage.includes('#')
      ? safeSentryUrl(rawPage, quoteToken, pageOrigin)
      : redactQuoteToken(rawPage, quoteToken);
    scope.setTag('page', safePage);
    scope.setTag('action', context?.action || 'unknown');

    // Path only. The href query would include the quoteID credential.
    scope.setContext('navigation', {
      url: safeSentryUrl(window.location.href, quoteToken, pageOrigin),
      referrer: document.referrer ? safeSentryUrl(document.referrer, quoteToken, pageOrigin) : document.referrer,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    });

    scope.setContext('ole_app', {
      environment: process.env.NODE_ENV || 'development',
      version: process.env.REACT_APP_VERSION || '1.0.0',
      sessionType: 'quote_session',
    });

    // Contexto específico del error
    if (context?.formData) {
      scope.setContext('form_data', {
        hasData: !!context.formData,
        fieldsCount: Object.keys(context.formData).length,
        // No incluir datos sensibles
      });
    }

    if (context?.apiEndpoint) {
      scope.setContext('api_call', {
        endpoint: safeSentryUrl(context.apiEndpoint, quoteToken, pageOrigin),
        method: 'POST', // Asumiendo POST para formularios
        timestamp: new Date().toISOString(),
      });
    }

    if (context?.additionalData) {
      scope.setContext('additional_context', scrubMonitorPayload(context.additionalData));
    }

    // Capturar el error
    Sentry.captureException(error instanceof Error ? error : new Error(error));
  });
};

/**
 * Captura errores de formularios OLE específicamente
 */
export const captureFormError = (
  error: Error | string,
  formName: string,
  formData?: any,
  additionalContext?: Record<string, any>
) => {
  if (!isSentryAvailable()) {
    sentryLog('debug', 'Sentry no está disponible, omitiendo captureFormError');
    return;
  }

  captureOLEError(error, {
    component: `${formName}_form`,
    action: 'form_submission',
    formData,
    ...additionalContext,
  });
};

/**
 * Captura errores de navegación OLE
 */
export const captureNavigationError = (
  error: Error | string,
  from: string,
  to: string,
  additionalContext?: Record<string, any>
) => {
  if (!isSentryAvailable()) {
    sentryLog('debug', 'Sentry no está disponible, omitiendo captureNavigationError');
    return;
  }

  captureOLEError(error, {
    component: 'navigation',
    action: 'page_transition',
    page: to,
    additionalData: {
      navigation: { from, to },
      ...additionalContext,
    },
  });
};

// ==================== FUNCIONALIDADES AVANZADAS DE SENTRY ====================

/**
 * Monitoreo de Core Web Vitals - Métricas de rendimiento críticas
 */
export const measureCoreWebVitals = () => {
  // Web Vitals se capturan automáticamente con browserMetricsIntegration
  // Pero podemos agregar mediciones personalizadas
  if ('web-vitals' in window) {
    import('web-vitals').then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
      getCLS((data) => sentryLog('log', 'Core Web Vital CLS:', data));
      getFID((data) => sentryLog('log', 'Core Web Vital FID:', data));
      getFCP((data) => sentryLog('log', 'Core Web Vital FCP:', data));
      getLCP((data) => sentryLog('log', 'Core Web Vital LCP:', data));
      getTTFB((data) => sentryLog('log', 'Core Web Vital TTFB:', data));
    });
  }
};

/**
 * Monitoreo de formularios críticos de seguros
 */
export const monitorInsuranceForm = (
  formId: string,
  formData: any,
  step: string
) => {
  if (!isSentryAvailable()) {
    sentryLog('debug', 'Sentry no está disponible, omitiendo monitorInsuranceForm');
    return;
  }

  // Solo breadcrumb (NO genera trace)
  addBreadcrumb(
    `Insurance form step: ${step}`,
    'insurance_form',
    'info',
    scrubMonitorPayload({
      formId,
      step,
      hasData: !!formData,
      fieldsCount: Object.keys(formData || {}).length,
      timestamp: new Date().toISOString(),
    })
  );
};

/**
 * Monitoreo de cálculos de cotizaciones
 * NOTA: Solo usa breadcrumbs, NO genera traces
 */
export const monitorQuoteCalculation = async (
  calculationType: string,
  inputData: any,
  callback: () => Promise<any>
) => {
  const startTime = performance.now();

  // Breadcrumb al inicio
  if (isSentryAvailable()) {
    addBreadcrumb(
      `Quote calculation started: ${calculationType}`,
      'quote_calculation',
      'info',
      scrubMonitorPayload({
        calculationType,
        inputSize: JSON.stringify(inputData).length,
        timestamp: new Date().toISOString(),
      })
    );
  }

  try {
    const result = await callback();
    const duration = performance.now() - startTime;

    // Breadcrumb de éxito
    if (isSentryAvailable()) {
      addBreadcrumb(
        `Quote calculation completed: ${calculationType}`,
        'quote_calculation',
        'info',
        scrubMonitorPayload({
          calculationType,
          success: true,
          duration,
          timestamp: new Date().toISOString(),
        })
      );
    }

    return result;
  } catch (error) {
    const duration = performance.now() - startTime;

    // Breadcrumb de error
    if (isSentryAvailable()) {
      addBreadcrumb(
        `Quote calculation failed: ${calculationType}`,
        'quote_calculation',
        'error',
        scrubMonitorPayload({
          calculationType,
          success: false,
          duration,
          error: error instanceof Error ? error.message : String(error),
          timestamp: new Date().toISOString(),
        })
      );
    }

    throw error;
  }
};

/**
 * Monitoreo de APIs críticas de seguros
 * NOTA: Solo usa breadcrumbs, NO genera traces
 */
export const monitorInsuranceAPI = async (
  endpoint: string,
  method: string,
  operation: string,
  callback: () => Promise<any>
) => {
  const startTime = performance.now();

  // Breadcrumb al inicio
  if (isSentryAvailable()) {
    addBreadcrumb(
      `API call started: ${operation}`,
      'insurance_api',
      'info',
      scrubMonitorPayload({
        endpoint,
        method,
        operation,
        timestamp: new Date().toISOString(),
      })
    );
  }

  try {
    const result = await callback();
    const duration = performance.now() - startTime;

    // Breadcrumb de éxito
    if (isSentryAvailable()) {
      addBreadcrumb(
        `API call completed: ${operation}`,
        'insurance_api',
        'info',
        scrubMonitorPayload({
          endpoint,
          method,
          operation,
          success: true,
          duration,
          timestamp: new Date().toISOString(),
        })
      );
    }

    return result;
  } catch (error) {
    const duration = performance.now() - startTime;

    // Breadcrumb de error
    if (isSentryAvailable()) {
      addBreadcrumb(
        `API call failed: ${operation}`,
        'insurance_api',
        'error',
        scrubMonitorPayload({
          endpoint,
          method,
          operation,
          success: false,
          duration,
          error: error instanceof Error ? error.message : String(error),
          statusCode: (error as any)?.response?.status,
          timestamp: new Date().toISOString(),
        })
      );
    }

    throw error;
  }
};

/**
 * Configurar feedback de usuario para reportar problemas
 */
export const setupUserFeedback = () => {
  if (!isSentryAvailable()) {
    sentryLog('debug', 'Sentry no está disponible, omitiendo setupUserFeedback');
    return;
  }

  // Configurar el widget de feedback de Sentry
  Sentry.showReportDialog({
    title: 'Reportar un Problema',
    subtitle: 'Ayúdanos a mejorar OLE reportando este problema.',
    subtitle2: 'Los datos sensibles serán protegidos.',
    labelName: 'Nombre',
    labelEmail: 'Email',
    labelComments: 'Describe el problema',
    labelSubmit: 'Enviar Reporte',
    successMessage: '¡Gracias! Tu reporte ha sido enviado.',
    errorGeneric: 'Ha ocurrido un error al enviar el reporte.',
    eventId: Sentry.lastEventId(),
  });
};

/**
 * Monitoreo de conversiones de seguros
 */
export const trackInsuranceConversion = (
  conversionType: 'quote_started' | 'quote_completed' | 'application_started' | 'application_completed' | 'policy_issued',
  data: Record<string, any>
) => {
  if (!isSentryAvailable()) {
    sentryLog('debug', 'Sentry no está disponible, omitiendo trackInsuranceConversion');
    return;
  }

  // Solo breadcrumb (NO genera trace, solo contexto para errores)
  addBreadcrumb(
    `Conversion: ${conversionType}`,
    'insurance_conversion',
    'info',
    scrubMonitorPayload({
      conversionType,
      ...data,
      timestamp: new Date().toISOString(),
    })
  );
};

/**
 * Records an application status change. The quoteID argument is only used to
 * strip that credential out of the breadcrumb.
 */
export const trackApplicationState = (
  quoteId: string,
  state: ApplicationStatus,
  previousState?: string,
  metadata?: Record<string, any>
) => {
  if (!isSentryAvailable()) {
    sentryLog('debug', 'Sentry no está disponible, omitiendo trackApplicationState');
    return;
  }

  const stateData = scrubMonitorPayload({
    currentState: state,
    previousState,
    stateChange: previousState ? `${previousState} → ${state}` : `initial: ${state}`,
    timestamp: new Date().toISOString(),
    ...metadata
  }, quoteId);

  addBreadcrumb(
    redactQuoteToken(`App state: ${previousState || 'none'} → ${state}`, quoteId),
    'application_state',
    'info',
    stateData
  );
};

/**
 * Monitoreo de abandono de formularios
 */
export const trackFormAbandonment = (
  formId: string,
  step: string,
  timeSpent: number,
  data: any
) => {
  if (!isSentryAvailable()) {
    sentryLog('debug', 'Sentry no está disponible, omitiendo trackFormAbandonment');
    return;
  }

  // Solo breadcrumb (NO genera trace, solo contexto para errores)
  addBreadcrumb(
    `Form abandonment: ${formId} at ${step}`,
    'form_abandonment',
    'warning',
    {
      formId,
      step,
      timeSpent,
      data,
      timestamp: new Date().toISOString(),
    }
  );
};

/**
 * Monitoreo de rendimiento de carga de página
 */
export const monitorPageLoadPerformance = () => {
  if ('performance' in window) {
    window.addEventListener('load', () => {
      setTimeout(() => {
        const perfData = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;

        if (perfData) {
          // Métricas de rendimiento (disponibles en versiones más nuevas)
          // Sentry.metrics.distribution('page_load_time', perfData.loadEventEnd - perfData.loadEventStart, {
          //   unit: 'millisecond'
          // });
          //
          // Sentry.metrics.distribution('dom_content_loaded', perfData.domContentLoadedEventEnd - perfData.domContentLoadedEventStart, {
          //   unit: 'millisecond'
          // });

          // Contexto adicional de rendimiento
          setGlobalContext('page_performance', {
            loadTime: perfData.loadEventEnd - perfData.loadEventStart,
            domContentLoaded: perfData.domContentLoadedEventEnd - perfData.domContentLoadedEventStart,
            firstPaint: performance.getEntriesByName('first-paint')[0]?.startTime,
            firstContentfulPaint: performance.getEntriesByName('first-contentful-paint')[0]?.startTime,
          });
        }
      }, 0);
    });
  }
};

/**
 * Monitoreo de memoria y rendimiento del navegador
 */
export const monitorBrowserPerformance = () => {
  if ('memory' in performance) {
    setInterval(() => {
      const memInfo = (performance as any).memory;

      // Métricas de memoria (disponibles en versiones más nuevas)
      // Sentry.metrics.gauge('browser_memory_used', memInfo.usedJSHeapSize, {
      //   unit: 'byte'
      // });
      //
      // Sentry.metrics.gauge('browser_memory_total', memInfo.totalJSHeapSize, {
      //   unit: 'byte'
      // });
      //
      // Sentry.metrics.gauge('browser_memory_limit', memInfo.jsHeapSizeLimit, {
      //   unit: 'byte'
      // });

      // Enviar como contexto adicional
      setGlobalContext('browser_memory', {
        used: memInfo.usedJSHeapSize,
        total: memInfo.totalJSHeapSize,
        limit: memInfo.jsHeapSizeLimit,
        timestamp: new Date().toISOString(),
      });
    }, 30000); // Cada 30 segundos
  }
};

/**
 * Hook personalizado para monitoreo de formularios OLE
 */
export const useFormMonitoring = (formId: string) => {
  const [startTime] = useState(() => Date.now());
  const [currentStep, setCurrentStep] = useState(1);

  const trackStepChange = (newStep: number, formData?: any) => {
    const timeSpent = Date.now() - startTime;

    if (isSentryAvailable()) {
      monitorInsuranceForm(formId, formData, `step_${newStep}`);

      addBreadcrumb(
        `Form step changed: ${currentStep} → ${newStep}`,
        'form_navigation',
        'info',
        {
          formId,
          fromStep: currentStep,
          toStep: newStep,
          timeSpent,
          timestamp: new Date().toISOString()
        }
      );
    } else {
      sentryLog('debug', 'Sentry no está disponible, omitiendo trackStepChange');
    }

    setCurrentStep(newStep);
  };

  const trackFormAbandonment = (reason?: string) => {
    const timeSpent = Date.now() - startTime;

    if (isSentryAvailable()) {
      // Solo breadcrumb (NO genera trace, solo contexto para errores)
      addBreadcrumb(
        `Form abandonment: ${formId} at step ${currentStep}`,
        'form_abandonment',
        'warning',
        {
          formId,
          step: `step_${currentStep}`,
          timeSpent,
          reason,
          lastStep: currentStep,
          timestamp: new Date().toISOString()
        }
      );
    } else {
      sentryLog('debug', 'Sentry no está disponible, omitiendo trackFormAbandonment');
    }
  };

  const trackFormCompletion = (completionData?: any) => {
    const timeSpent = Date.now() - startTime;

    if (isSentryAvailable()) {
      addBreadcrumb(
        `Form completed: ${formId}`,
        'form_completion',
        'info',
        {
          formId,
          totalSteps: currentStep,
          timeSpent,
          completionData,
          timestamp: new Date().toISOString()
        }
      );
    } else {
      sentryLog('debug', 'Sentry no está disponible, omitiendo trackFormCompletion');
    }
  };

  return {
    trackStepChange,
    trackFormAbandonment,
    trackFormCompletion,
    currentStep
  };
};

/**
 * Monitoreo específico para aplicaciones de seguros
 */
/**
 * Monitoreo específico para aplicaciones de seguros
 * NOTA: Solo usa breadcrumbs, NO genera traces
 */
export const monitorApplicationFlow = (
  applicationId: string,
  currentPage: number,
  totalPages: number,
  formData?: any
) => {
  if (!isSentryAvailable()) {
    sentryLog('debug', 'Sentry no está disponible, omitiendo monitorApplicationFlow');
    return;
  }

  // Solo breadcrumb (NO genera trace)
  addBreadcrumb(
    `Application progress: ${currentPage}/${totalPages}`,
    'application_progress',
    'info',
    scrubMonitorPayload({
      applicationId,
      currentPage,
      totalPages,
      progress: (currentPage / totalPages) * 100,
      timestamp: new Date().toISOString()
    })
  );
};

/**
 * Configurar alertas inteligentes
 */
export const setupSmartAlerts = () => {
  if (!isSentryAvailable()) {
    sentryLog('debug', 'Sentry no está disponible, omitiendo setupSmartAlerts');
    return;
  }

  // Configurar alertas basadas en métricas
  // Esto se configura en el dashboard de Sentry, pero podemos documentar las métricas importantes

  const importantMetrics = [
    'quote_calculation_success',
    'quote_calculation_error',
    'insurance_api_success',
    'insurance_api_error',
    'form_abandonment',
    'insurance_conversion',
    'page_load_time',
    'browser_memory_used'
  ];

  sentryLog('log', 'Sentry metrics configured for OLE:', importantMetrics);
};

const sentryUtils = {
  initSentry,
  markSentryInitialized,
  captureError,
  captureApiError,
  captureReactError,
  captureOLEError,
  captureFormError,
  captureNavigationError,
  measureFunctionPerformance,
  measureApiCall,
  addBreadcrumb,
  trackUserAction,
  trackNavigation,
  trackStateChange,
  trackFormEvent,
  setQuoteInfo,
  setUserInfo,
  clearUserInfo,
  setGlobalContext,
  setGlobalTag,
  // Funcionalidades avanzadas
  measureCoreWebVitals,
  monitorInsuranceForm,
  monitorQuoteCalculation,
  monitorInsuranceAPI,
  setupUserFeedback,
  trackInsuranceConversion,
  trackApplicationState,
  trackFormAbandonment,
  monitorPageLoadPerformance,
  monitorBrowserPerformance,
  setupSmartAlerts,
  // Hooks y utilidades específicas de OLE
  useFormMonitoring,
  monitorApplicationFlow
};

export default sentryUtils;
